import { BaseService } from './BaseService';
import type { PagedResult } from '../types/tenant';

/**
 * Health status values.
 */
export const HealthStatus = {
  Unknown: 'Unknown',
  Healthy: 'Healthy',
  Unhealthy: 'Unhealthy',
  Degraded: 'Degraded',
} as const;

export type HealthStatus = typeof HealthStatus[keyof typeof HealthStatus];

/**
 * MCP server DTO.
 */
export interface McpServerDto {
  id: string;
  name: string;
  description?: string;
  serverUrl: string;
  headersJson?: string;
  isActive: boolean;
  lastHealthCheckAt?: string;
  healthStatus: string;
  healthCheckEndpoint?: string;
  healthCheckIntervalMinutes?: number;
  createdAt: string;
  updatedAt: string;
  toolCount: number;
  tools?: McpServerToolDto[];
}

/**
 * MCP server tool DTO.
 */
export interface McpServerToolDto {
  id: string;
  mcpServerId: string;
  toolName: string;
  description?: string;
  inputSchemaJson?: string;
  discoveredAt: string;
  lastUsedAt?: string;
}

/**
 * Agent MCP server assignment DTO.
 */
export interface AgentMcpServerAssignmentDto {
  id: string;
  agentDefinitionId: string;
  mcpServerId: string;
  mcpServerName: string;
  isEnabled: boolean;
}

/**
 * Agent assignment when viewing from MCP server detail (agents using this server).
 */
export interface McpServerAgentAssignmentDto {
  id: string;
  agentDefinitionId: string;
  agentName: string;
  isEnabled: boolean;
}

/**
 * MCP health result.
 */
export interface McpHealthResult {
  isHealthy: boolean;
  responseTimeMs: number;
  errorMessage?: string;
  checkedAt: string;
}

/**
 * Create MCP server request.
 */
export interface CreateMcpServerRequest {
  name: string;
  description?: string;
  serverUrl: string;
  authenticationJson?: string;
  headersJson?: string;
  isActive: boolean;
  healthCheckEndpoint?: string;
  healthCheckIntervalMinutes?: number;
  metadataJson?: string;
}

/**
 * Update MCP server request.
 */
export interface UpdateMcpServerRequest {
  name: string;
  description?: string;
  serverUrl: string;
  authenticationJson?: string;
  headersJson?: string;
  isActive: boolean;
  healthCheckEndpoint?: string;
  healthCheckIntervalMinutes?: number;
  metadataJson?: string;
}

/**
 * MCP server filters.
 */
export interface McpServerFilters {
  pageNumber?: number;
  pageSize?: number;
  isActive?: boolean;
  searchTerm?: string;
}

/**
 * Assign MCP to agent request.
 */
export interface AssignMcpToAgentRequest {
  mcpServerId: string;
  isEnabled?: boolean;
}

/**
 * MCP service for managing MCP servers and assignments.
 */
export class McpService extends BaseService {
  /**
   * Gets a paginated list of MCP servers with optional filtering.
   */
  async getMcpServers(filters?: McpServerFilters): Promise<PagedResult<McpServerDto>> {
    const queryParams = new URLSearchParams();
    if (filters?.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());
    if (filters?.searchTerm) queryParams.append('searchTerm', filters.searchTerm);

    const url = `/mcp-servers${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<McpServerDto>>(url);
  }

  /**
   * Gets an MCP server by ID.
   */
  async getMcpServer(id: string): Promise<McpServerDto> {
    return this.get<McpServerDto>(`/mcp-servers/${id}`);
  }

  /**
   * Creates a new MCP server.
   */
  async createMcpServer(request: CreateMcpServerRequest): Promise<McpServerDto> {
    return this.post<McpServerDto>('/mcp-servers', request);
  }

  /**
   * Updates an existing MCP server.
   */
  async updateMcpServer(id: string, request: UpdateMcpServerRequest): Promise<McpServerDto> {
    return this.put<McpServerDto>(`/mcp-servers/${id}`, request);
  }

  /**
   * Deletes an MCP server.
   */
  async deleteMcpServer(id: string): Promise<void> {
    return this.delete(`/mcp-servers/${id}`);
  }

  /**
   * Discovers tools from an MCP server.
   */
  async discoverTools(serverId: string): Promise<{ toolsDiscovered: number }> {
    return this.post<{ toolsDiscovered: number }>(`/mcp-servers/${serverId}/discover`);
  }

  /**
   * Performs a health check on an MCP server.
   */
  async checkHealth(serverId: string): Promise<McpHealthResult> {
    return this.post<McpHealthResult>(`/mcp-servers/${serverId}/health-check`);
  }

  /**
   * Gets MCP servers assigned to an agent.
   */
  async getAgentMcpServers(agentId: string): Promise<AgentMcpServerAssignmentDto[]> {
    return this.get<AgentMcpServerAssignmentDto[]>(`/mcp-servers/agents/${agentId}`);
  }

  /**
   * Assigns an MCP server to an agent.
   */
  async assignMcpToAgent(agentId: string, request: AssignMcpToAgentRequest): Promise<AgentMcpServerAssignmentDto> {
    return this.post<AgentMcpServerAssignmentDto>(`/mcp-servers/agents/${agentId}`, request);
  }

  /**
   * Gets agents assigned to an MCP server.
   */
  async getMcpServerAgents(serverId: string): Promise<McpServerAgentAssignmentDto[]> {
    return this.get<McpServerAgentAssignmentDto[]>(`/mcp-servers/${serverId}/agents`);
  }

  /**
   * Removes an MCP server assignment from an agent.
   */
  async removeMcpFromAgent(agentId: string, assignmentId: string): Promise<void> {
    return this.delete(`/mcp-servers/agents/${agentId}/${assignmentId}`);
  }
}

export const mcpService = new McpService();
